import { format as formatDateFns, toZonedTime, fromZonedTime } from 'date-fns-tz';
import { formatRelative as formatRelativeDateFns } from 'date-fns';

interface FormatOptions {
  timezone?: string;
  format?: string;
}

function formatWithTimezone(date: Date | string | null, format: string, timezone: string | null) {
  if (!date) {
    return null;
  }

  const zonedDate = timezone ? toZonedTime(date, timezone) : new Date(date);

  if (isNaN(zonedDate.getTime())) {
    return null;
  }

  return formatDateFns(zonedDate, format, { timeZone: timezone || undefined });
}

export function formatDate(date: Date | string | null, options?: FormatOptions) {
  const { timezone = Intl.DateTimeFormat().resolvedOptions().timeZone, format = 'MMM dd, yyyy' } =
    options || {};
  return formatWithTimezone(date, format, timezone);
}

export function formatTime(date: Date | string | null, options?: FormatOptions) {
  const { timezone = Intl.DateTimeFormat().resolvedOptions().timeZone, format = 'h:mmaaa' } =
    options || {};
  return formatWithTimezone(date, format, timezone);
}

export function formatDateTime(date: Date | string | null, options?: FormatOptions) {
  const {
    timezone = Intl.DateTimeFormat().resolvedOptions().timeZone,
    format = 'MMM dd, yyyy h:mmaaa'
  } = options || {};
  return formatWithTimezone(date, format, timezone);
}

/**
 * Format a date relative to the current date.
 * @param date - The date to format.
 * @param format - The format to use for dates that are more than 1 week past/in the future.
 * @returns The formatted date.
 */
export function formatRelative(date: string, format: string | null = null): string {
  const result = formatRelativeDateFns(fromZonedTime(date, 'UTC'), new Date());

  if (format) {
    // formatRelativeDateFns returns a string like "12/31/2017" for dates that are more than 1 week past/in the future.
    if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(result)) {
      return formatDateFns(fromZonedTime(date, 'UTC'), format);
    }
  }

  return result;
}
